"use client";

import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/auth";
import { useEffectiveAccount } from "@/hooks/auth/useEffectiveAddress";
import { cn } from "@/lib/utils";

/**
 * Persistent top banner shown during dev impersonation.
 * Displays the impersonated user's nickname and wallet address,
 * with an exit button to restore the dev's original session.
 *
 * Cannot be dismissed -- always visible when auth.isImpersonating === true.
 * Should be rendered above TopNav in the layout.
 */
export const ImpersonationBanner: React.FC = () => {
  const t = useTranslations("modals");
  const { isImpersonating, user, endImpersonation } = useAuth();
  // isFallback: target wallet is malformed — showing connected wallet's chain data instead
  const { isFallback } = useEffectiveAccount();

  if (!isImpersonating) {
    return null;
  }

  return (
    <div
      role="alert"
      className={cn(
        "w-full px-4 py-2",
        "bg-semantic-alert/20 border-b border-semantic-alert/40",
        "flex items-center justify-between gap-3",
      )}
    >
      <div className="flex items-center gap-2 text-sm min-w-0">
        <span className="text-semantic-alert font-semibold shrink-0">
          {t("impersonationBanner.viewingAs")}
        </span>

        {/* Vertical sub-flex stacks label/user/sub-line predictably (Fix #9) */}
        <div className="flex flex-col min-w-0">
          <span className="text-text-primary truncate">
            {user?.nickname && (
              <>
                {user.nickname}{" "}
                <span
                  className="text-text-secondary text-xs font-mono"
                  dir="ltr"
                >
                  ({user.wallet_address})
                </span>
              </>
            )}
            {!user?.nickname && (
              <span className="font-mono text-xs" dir="ltr">
                {user?.wallet_address}
              </span>
            )}
          </span>

          {/* Sub-line — chain-data note OR fallback warning (Fix #3) */}
          {isFallback ? (
            <span className="text-text-warning text-xs">
              {t("impersonationBanner.invalidTarget")}
            </span>
          ) : (
            <span className="text-text-secondary text-xs">
              {t("impersonationBanner.blockchainDataNote")}
            </span>
          )}
        </div>
      </div>

      <Button
        type="button"
        variant="solid"
        colors="secondary"
        size="sm"
        className="shrink-0"
        onClick={endImpersonation}
      >
        {t("impersonationBanner.exit")}
      </Button>
    </div>
  );
};
